import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { getRackUsage } from "../domain/placement";
import { getRackDisplayName } from "../domain/rack-settings";
import type { MapBorderStyle, MapDefinition, MapLabelWrapMode, Placement, RackSettings } from "../domain/types";
import { useUiLanguage } from "../i18n";

interface FloorplanProps {
    readonly map: MapDefinition;
    readonly placements: readonly Placement[];
    readonly rackSettings: readonly RackSettings[];
    readonly selectedRackId: string | null;
    readonly onSelectRack: (rackId: string, openDesigner: boolean) => void;
}

interface Point {
    readonly x: number;
    readonly y: number;
}

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 1.8;

function clampZoom(value: number): number {
    return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
}

function getStateClass(usedU: number, totalU: number): string {
    if (usedU === 0) {
        return "is-free";
    }
    return usedU >= totalU ? "is-full" : "is-partial";
}

function dashPattern(style: MapBorderStyle, scale = 1): string | undefined {
    if (style === "dashed") {
        return `${11 * scale} ${8 * scale}`;
    }
    if (style === "dotted") {
        return `${2 * scale} ${7 * scale}`;
    }
    return undefined;
}

function wrapLabel(
    value: string,
    wrapMode: MapLabelWrapMode,
    maxWidth: number,
    fontSize: number,
    maxLines: number,
): readonly string[] {
    const explicitLines = value.replaceAll("\r", "").split("\n").slice(0, 8);
    if (wrapMode === "none") {
        return explicitLines.slice(0, maxLines);
    }
    const charactersPerLine = Math.max(1, Math.floor(maxWidth / Math.max(1, fontSize * 0.58)));
    const lines: string[] = [];
    for (const explicitLine of explicitLines) {
        const words = explicitLine.trim().split(/\s+/).slice(0, 128);
        let currentLine = "";
        for (const word of words) {
            const candidate = currentLine.length === 0 ? word : `${currentLine} ${word}`;
            if (currentLine.length > 0 && candidate.length > charactersPerLine) {
                lines.push(currentLine);
                currentLine = word;
            } else {
                currentLine = candidate;
            }
        }
        lines.push(currentLine);
    }
    return lines.slice(0, maxLines);
}

export function Floorplan({
    map,
    placements,
    rackSettings,
    selectedRackId,
    onSelectRack,
}: FloorplanProps): React.JSX.Element {
    const { entity, t } = useUiLanguage();
    const mapName = entity("map", map.id, map.name);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState<Point>({ x: 36, y: 36 });
    const [isPanning, setIsPanning] = useState(false);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<{ x: number; y: number; offset: Point } | null>(null);

    const fitView = useCallback((): void => {
        const scroller = scrollerRef.current;
        if (scroller === null || scroller.clientWidth <= 0 || scroller.clientHeight <= 0) {
            return;
        }
        const padding = 32;
        const nextZoom = clampZoom(
            Math.min(
                1,
                Math.max(1, scroller.clientWidth - padding * 2) / map.width,
                Math.max(1, scroller.clientHeight - padding * 2) / map.height,
            ),
        );
        setZoom(nextZoom);
        setOffset({
            x: (scroller.clientWidth - map.width * nextZoom) / 2,
            y: (scroller.clientHeight - map.height * nextZoom) / 2,
        });
    }, [map.height, map.width]);

    useLayoutEffect(() => {
        fitView();
    }, [fitView]);

    function startPan(event: React.PointerEvent<HTMLDivElement>): void {
        const target = event.target as Element;
        if (target.closest("[data-rack-id]") !== null || scrollerRef.current === null) {
            return;
        }
        dragRef.current = {
            x: event.clientX,
            y: event.clientY,
            offset,
        };
        setIsPanning(true);
        event.currentTarget.setPointerCapture(event.pointerId);
    }

    function pan(event: React.PointerEvent<HTMLDivElement>): void {
        const origin = dragRef.current;
        const scroller = scrollerRef.current;
        if (origin === null || scroller === null) {
            return;
        }
        setOffset({
            x: origin.offset.x + event.clientX - origin.x,
            y: origin.offset.y + event.clientY - origin.y,
        });
    }

    function endPan(): void {
        dragRef.current = null;
        setIsPanning(false);
    }

    function zoomAt(nextZoom: number, anchor: Point): void {
        const safeZoom = clampZoom(nextZoom);
        const ratio = safeZoom / zoom;
        setOffset({
            x: anchor.x - (anchor.x - offset.x) * ratio,
            y: anchor.y - (anchor.y - offset.y) * ratio,
        });
        setZoom(safeZoom);
    }

    function resetView(): void {
        fitView();
    }

    return (
        <section className="workspace-card floorplan-panel" aria-labelledby="floorplan-title">
            <h2 id="floorplan-title" className="sr-only">
                {mapName}
            </h2>
            <div className="floorplan-tools" role="group" aria-label={t("floorplanZoomControls")}>
                <output aria-label={t("currentZoom")}>{Math.round(zoom * 100)}%</output>
                <button type="button" className="button" onClick={resetView}>
                    {t("resetView")}
                </button>
            </div>
            <div
                ref={scrollerRef}
                className={`floorplan-scroller${isPanning ? " is-panning" : ""}`}
                role="application"
                // biome-ignore lint/a11y/noNoninteractiveTabindex: The map canvas exposes keyboard pan and zoom controls.
                tabIndex={0}
                aria-label={t("panZoomFloorplan")}
                onPointerDown={startPan}
                onPointerMove={pan}
                onPointerUp={endPan}
                onPointerCancel={endPan}
                onWheel={(event) => {
                    event.preventDefault();
                    const bounds = event.currentTarget.getBoundingClientRect();
                    zoomAt(zoom * (event.deltaY < 0 ? 1.1 : 0.9), {
                        x: event.clientX - bounds.left,
                        y: event.clientY - bounds.top,
                    });
                }}
                onKeyDown={(event) => {
                    if (event.key === "+" || event.key === "=") {
                        event.preventDefault();
                        zoomAt(zoom + 0.1, {
                            x: event.currentTarget.clientWidth / 2,
                            y: event.currentTarget.clientHeight / 2,
                        });
                    } else if (event.key === "-") {
                        event.preventDefault();
                        zoomAt(zoom - 0.1, {
                            x: event.currentTarget.clientWidth / 2,
                            y: event.currentTarget.clientHeight / 2,
                        });
                    } else if (event.key === "0") {
                        event.preventDefault();
                        resetView();
                    } else if (event.key.startsWith("Arrow")) {
                        event.preventDefault();
                        setOffset((value) => ({
                            x: value.x + (event.key === "ArrowLeft" ? 40 : event.key === "ArrowRight" ? -40 : 0),
                            y: value.y + (event.key === "ArrowUp" ? 40 : event.key === "ArrowDown" ? -40 : 0),
                        }));
                    }
                }}
            >
                <svg
                    className="floorplan-svg"
                    style={{
                        width: map.width,
                        height: map.height,
                        transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${zoom})`,
                        transformOrigin: "0 0",
                    }}
                    viewBox={`0 0 ${map.width} ${map.height}`}
                    role="img"
                    aria-label={`${mapName} ${t("interactiveFloorplan")}`}
                >
                    <defs>
                        <pattern
                            id={`${map.id}-minor-grid`}
                            width={map.gridSize}
                            height={map.gridSize}
                            patternUnits="userSpaceOnUse"
                        >
                            <path
                                d={`M${map.gridSize} 0H0V${map.gridSize}`}
                                fill="none"
                                stroke="rgba(255,255,255,.025)"
                                strokeWidth="1"
                            />
                        </pattern>
                        <pattern
                            id={`${map.id}-major-grid`}
                            width={map.gridSize * 5}
                            height={map.gridSize * 5}
                            patternUnits="userSpaceOnUse"
                        >
                            <rect
                                width={map.gridSize * 5}
                                height={map.gridSize * 5}
                                fill={`url(#${map.id}-minor-grid)`}
                            />
                            <path
                                d={`M${map.gridSize * 5} 0H0V${map.gridSize * 5}`}
                                fill="none"
                                stroke="rgba(255,255,255,.055)"
                                strokeWidth="1.4"
                            />
                        </pattern>
                        <filter id="rack-shadow" x="-30%" y="-30%" width="160%" height="160%">
                            <feDropShadow dx="0" dy="7" stdDeviation="7" floodColor="#000" floodOpacity=".45" />
                        </filter>
                    </defs>
                    <rect width={map.width} height={map.height} fill={`url(#${map.id}-major-grid)`} />
                    {map.zones.map((zone) => (
                        <g
                            key={zone.id}
                            className="availability-zone"
                            style={{ "--zone-color": zone.color } as React.CSSProperties}
                        >
                            <rect x={zone.x} y={zone.y} width={zone.width} height={zone.height} rx="12" />
                            <text x={zone.x + 14} y={zone.y + 27}>
                                {entity("zone", zone.id, zone.name)}
                            </text>
                        </g>
                    ))}
                    {map.areas.map((area) => {
                        const name = entity("area", area.id, area.name);
                        const lines = wrapLabel(name, area.wrapMode, area.maxWidth, area.fontSize, area.maxLines);
                        const lineStep = area.fontSize * area.lineHeight;
                        const centered = area.labelPosition === "center";
                        const labelX = centered ? area.x + area.width / 2 : area.x + 14;
                        const labelY = centered
                            ? area.y + area.height / 2 - ((lines.length - 1) * lineStep) / 2
                            : area.y + 26;
                        return (
                            <g key={area.id} className={`map-area is-${area.borderStyle}`}>
                                <rect
                                    x={area.x}
                                    y={area.y}
                                    width={area.width}
                                    height={area.height}
                                    rx="8"
                                    fill={area.color}
                                    fillOpacity={area.fillOpacity / 100}
                                    stroke={area.color}
                                    strokeOpacity={area.borderOpacity / 100}
                                    strokeWidth={area.borderWidth}
                                    strokeDasharray={dashPattern(area.borderStyle)}
                                />
                                <text
                                    x={labelX}
                                    y={labelY}
                                    fill={area.color}
                                    fontSize={area.fontSize}
                                    textAnchor={centered ? "middle" : "start"}
                                    dominantBaseline={centered ? "middle" : "auto"}
                                >
                                    {lines.map((line, index) => (
                                        <tspan key={`${area.id}-${index}`} x={labelX} dy={index === 0 ? 0 : lineStep}>
                                            {line.toUpperCase()}
                                        </tspan>
                                    ))}
                                </text>
                            </g>
                        );
                    })}
                    {map.lines.map((line) => (
                        <line
                            key={line.id}
                            className="map-guide-line"
                            x1={line.x1}
                            x2={line.x2}
                            y1={line.y1}
                            y2={line.y2}
                            stroke={line.color}
                            strokeWidth={line.thickness}
                            strokeDasharray={dashPattern(line.style)}
                        />
                    ))}
                    {map.badges.map((badge) => (
                        <g key={badge.id} className="map-badge">
                            <rect
                                x={badge.x}
                                y={badge.y}
                                width={badge.width}
                                height={badge.height}
                                rx={badge.cornerRadius}
                                fill={badge.color}
                                fillOpacity={badge.fillOpacity / 100}
                                stroke={badge.color}
                                strokeWidth={badge.borderThickness}
                                strokeDasharray={dashPattern(badge.borderStyle)}
                            />
                            <text
                                x={badge.x + badge.width / 2}
                                y={badge.y + badge.height / 2}
                                fill={badge.textColor}
                                fontSize={badge.fontSize}
                                textAnchor="middle"
                                dominantBaseline="middle"
                            >
                                {entity("badge", badge.id, badge.name).toUpperCase()}
                            </text>
                        </g>
                    ))}
                    {map.labels.map((label) => {
                        const lines = wrapLabel(
                            entity("label", label.id, label.name),
                            label.wrapMode,
                            label.maxWidth,
                            label.fontSize,
                            label.maxLines,
                        );
                        return (
                            <text
                                key={label.id}
                                className="map-free-label"
                                x={label.x}
                                y={label.y}
                                fill={label.color}
                                fontSize={label.fontSize}
                                textAnchor={
                                    label.alignment === "center"
                                        ? "middle"
                                        : label.alignment === "right"
                                          ? "end"
                                          : "start"
                                }
                            >
                                {lines.map((line, index) => (
                                    <tspan
                                        key={`${label.id}-${index}`}
                                        x={label.x}
                                        dy={index === 0 ? 0 : label.fontSize * label.lineHeight}
                                    >
                                        {line}
                                    </tspan>
                                ))}
                            </text>
                        );
                    })}
                    {map.walls.map((wall) => (
                        <line
                            key={wall.id}
                            className={`map-wall is-${wall.kind}`}
                            x1={wall.x1}
                            x2={wall.x2}
                            y1={wall.y1}
                            y2={wall.y2}
                        />
                    ))}
                    {map.racks.map((rack) => {
                        const usage = getRackUsage(rack, placements);
                        const selected = rack.id === selectedRackId;
                        const componentLabel = usage.componentCount === 1 ? t("component") : t("components");
                        const itemLabel = usage.componentCount === 1 ? t("item") : t("items");
                        const rackName = getRackDisplayName(rack, rackSettings, entity("rack", rack.id, rack.name));
                        return (
                            <g
                                key={rack.id}
                                data-rack-id={rack.id}
                                className={`floor-rack ${getStateClass(usage.usedU, rack.sizeU)}${selected ? " is-selected" : ""}`}
                                transform={`rotate(${rack.rotation} ${rack.x + rack.width / 2} ${rack.y + rack.depth / 2})`}
                                role="button"
                                tabIndex={0}
                                aria-label={`${rackName}, ${usage.componentCount} ${componentLabel}, ${usage.usedU} of ${rack.sizeU} ${t("rackUnitsUsed")}`}
                                onClick={() => onSelectRack(rack.id, true)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        onSelectRack(rack.id, true);
                                    }
                                }}
                            >
                                <title>
                                    {rackName} · {usage.componentCount} {componentLabel} · {usage.usedU}/{rack.sizeU}U
                                </title>
                                <rect
                                    x={rack.x}
                                    y={rack.y}
                                    width={rack.width}
                                    height={rack.depth}
                                    rx="7"
                                    filter="url(#rack-shadow)"
                                />
                                <rect
                                    className="rack-inner"
                                    x={rack.x + 8}
                                    y={rack.y + 8}
                                    width={rack.width - 16}
                                    height={rack.depth - 16}
                                    rx="3"
                                />
                                <path
                                    className="rack-vents"
                                    d={`M${rack.x + 16} ${rack.y + 22}H${rack.x + rack.width - 16} M${rack.x + 16} ${rack.y + rack.depth - 22}H${rack.x + rack.width - 16}`}
                                />
                                <text
                                    className="rack-name"
                                    x={rack.x + rack.width / 2}
                                    y={rack.y + rack.depth / 2 - 6}
                                    transform={`rotate(${-rack.rotation} ${rack.x + rack.width / 2} ${rack.y + rack.depth / 2})`}
                                >
                                    {rackName}
                                </text>
                                <text
                                    className="rack-usage"
                                    x={rack.x + rack.width / 2}
                                    y={rack.y + rack.depth / 2 + 14}
                                    transform={`rotate(${-rack.rotation} ${rack.x + rack.width / 2} ${rack.y + rack.depth / 2})`}
                                >
                                    {usage.componentCount} {itemLabel} · {usage.usedU}/{rack.sizeU}U
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
            <footer className="floorplan-legend">
                <div className="rack-status-key" role="list" aria-label={t("rackStatusLegend")}>
                    <span>
                        <i className="legend-free" />
                        {t("free")}
                    </span>
                    <span>
                        <i className="legend-partial" />
                        {t("partial")}
                    </span>
                    <span>
                        <i className="legend-full" />
                        {t("full")}
                    </span>
                    <span className="legend-hint">{t("openRackHint")}</span>
                </div>
            </footer>
        </section>
    );
}
